// 🚨 This is used for just-in-time (JIT) utilities that can be fetched on a user event or in the background when assignment isn't needed immediately on page load.
// This should only be loaded using the `window.cpap.dynamicallyLoadScript()` method, and only for background logic. Use an `onload` callback to handle deferred logic.
window.cpap.onDocumentReady(() => {
  window.cpap = window.cpap || {};

  window.cpap.visualEffects = {
    /**
     * Fades in one or more elements by adding animation classes.
     *
     * @function
     * @param {string|Element|NodeList|Array} nodesOrQuery - A CSS selector, a DOM node, or a collection of nodes.
     * @example
     * // Fade in elements using a CSS selector
     * window.cpap.visualEffects.fadeIn('.fadeable');
     *
     * @example
     * // Fade in a single DOM element
     * const element = document.querySelector('#fade-in');
     * window.cpap.visualEffects.fadeIn(element);
     *
     * @description
     * Removes the `cpap__fadeable--hidden` class and adds the `cpap__fadeable--faded-in` class to each node.
     */
    fadeIn: (nodesOrQuery) => {
      const nodes = window.cpap.getNodesFromNodesOrQuery(nodesOrQuery);

      nodes.forEach((node) => {
        node.classList.remove('cpap__fadeable--hidden');
        node.classList.add('cpap__fadeable--faded-in');
      });
    },

    /**
     * Fades out one or more elements by adding animation classes.
     *
     * @function
     * @param {string|Element|NodeList|Array} nodesOrQuery - A CSS selector, a DOM node, or a collection of nodes.
     * @example
     * // Fade out elements using a CSS selector
     * window.cpap.visualEffects.fadeOut('.fadeable');
     *
     * @example
     * // Fade out a single DOM element
     * const element = document.querySelector('#fade-out');
     * window.cpap.visualEffects.fadeOut(element);
     *
     * @description
     * Removes the `cpap__fadeable--faded-in` class and adds the `cpap__fadeable--hidden` class to each node.
     */
    fadeOut: (nodesOrQuery) => {
      const nodes = window.cpap.getNodesFromNodesOrQuery(nodesOrQuery);

      nodes.forEach((node) => {
        node.classList.remove('cpap__fadeable--faded-in');
        node.classList.add('cpap__fadeable--hidden');
      });
    },

    /**
     * Visually displays a node or set of nodes using display utility classes.
     *
     * @function
     * @param {string|Element|NodeList|Array} nodesOrQuery - A CSS selector, a DOM node, or a collection of nodes.
     * @example
     * // Fade out elements using a CSS selector
     * window.cpap.visualEffects.show('.expample-form');
     *
     * @example
     * // Show a single DOM element
     * const element = document.querySelector('#example-form');
     * window.cpap.visualEffects.show(element);
     *
     * @description
     * Removes the `d-block` class and adds the `d-none` class to each node.
     * This can be extended for other classes such as `d-flex`, etc. in the future if needed.
     */
    show: (nodesOrQuery) => {
      const nodes = window.cpap.getNodesFromNodesOrQuery(nodesOrQuery);

      nodes.forEach((node) => {
        node.classList.remove('d-none');

        const computedDisplay = window.getComputedStyle(node).display;

        if (computedDisplay === 'flex') {
          node.classList.add('d-flex');
        } else {
          node.classList.add('d-block');
        }
      });
    },

    /**
     * Visually hides a node or set of nodes using display utility classes.
     *
     * @function
     * @param {string|Element|NodeList|Array} nodesOrQuery - A CSS selector, a DOM node, or a collection of nodes.
     * @example
     * // Fade out elements using a CSS selector
     * window.cpap.visualEffects.hide('.example-form');
     *
     * @example
     * // Hide a single DOM element
     * const element = document.querySelector('#example-form');
     * window.cpap.visualEffects.hide(element);
     *
     * @description
     * Add the `d-none` class and removes the `d-block` and `d-flex` class from each node.
     * This can be extended for other classes such as `d-flex`, etc. in the future if needed.
     */
    hide: (nodesOrQuery) => {
      const nodes = window.cpap.getNodesFromNodesOrQuery(nodesOrQuery);

      nodes.forEach((node) => {
        node.classList.remove('d-block');
        node.classList.remove('d-flex');
        node.classList.add('d-none');
      });
    },
  };

  const getRecentlyViewedProducts = () => {
    const localStorageData = localStorage.getItem('recently_viewed_products');
    const viewedProducts = localStorageData ? JSON.parse(localStorageData) : [];
    return viewedProducts.slice(0, 12); // Limit to 12 products
  };

  window.cpap.addRecentlyViewedProduct = async (product) => {
    await window.scheduler.yield();

    const viewedProducts = getRecentlyViewedProducts();

    await window.scheduler.yield();

    // Remove product if it already exists in the array
    const updatedProducts = viewedProducts.filter(
      ({ productId }) => productId !== product.productId
    );

    // Add the new product to the beginning of the array
    updatedProducts.unshift(product);

    // Update localStorage
    localStorage.setItem('recently_viewed_products', JSON.stringify(updatedProducts));
  };

  /**
   * Get the dynamic offset for scrolling on the pdp sticky menu based on multiple conditions.
   */
  window.cpap.getPdpStickyMenuScrollOffset = (newScrollToLocation, previousScrollLocation) => {
    const willScrollUp = newScrollToLocation < previousScrollLocation;

    const isHoveredOverStickyMenuDownMinimized = document.querySelector(
      '.cpap-scroll-to-down--hovered'
    );
    const isHoveredOverStickyMenuExpanded = document.querySelector('.cpap-scroll-to-top--hovered');
    const isHoveredOverStickyMenu =
      isHoveredOverStickyMenuDownMinimized || isHoveredOverStickyMenuExpanded;
    const stickyMenuExpandedScrollOffset = 193;
    const stickyMenuMinimizedScrollOffset = 65;

    const scrollOffset = isHoveredOverStickyMenu
      ? isHoveredOverStickyMenuExpanded
        ? stickyMenuExpandedScrollOffset
        : stickyMenuMinimizedScrollOffset
      : willScrollUp
      ? stickyMenuExpandedScrollOffset
      : stickyMenuMinimizedScrollOffset;

    return scrollOffset;
  };

  window.cpap.scrollToPdpTargetNode = (targetNode, scrollDelay = 100) => {
    const scrollToLocation = window.cpap.getScrollToLocation(targetNode);
    const scrollOffset = window.cpap.getPdpStickyMenuScrollOffset(scrollToLocation, window.scrollY);

    setTimeout(() => {
      window.scrollTo({
        top: window.cpap.getScrollToLocation(targetNode, scrollOffset),
        behavior: 'smooth',
      });
    }, scrollDelay);
  };
});
